import React from 'react'
import { ExecutionLogEntry } from '../types'

interface Props {
  executionLog: ExecutionLogEntry[]
}

/**
 * Transfer Execution Log Display Component
 *
 * Displays formatted transfer execution logs with:
 * - Color-coded status indicators
 * - Field mappings
 * - Database verification results
 *
 * @example
 * <TransferExecutionLogDisplay executionLog={logs} />
 */
export const TransferExecutionLogDisplay: React.FC<Props> = ({ executionLog }) => {
  return (
    <div style={{
      background: '#1e1e1e',
      padding: '20px',
      borderRadius: '4px',
      maxHeight: '1200px',
      overflow: 'auto',
      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
      fontSize: '13px',
      lineHeight: '1.8',
      textAlign: 'left'
    }}>
      {executionLog.map((entry) => {
        const statusColor = entry.status === 'success' ? '#08D6A1' :
                           entry.status === 'error' ? '#FA5953' : '#747678'
        const statusIcon = entry.status === 'success' ? '✓' :
                          entry.status === 'error' ? '✗' : '→'

        return (
          <div key={entry.step} style={{ marginBottom: '12px' }}>
            <div style={{ marginBottom: '4px' }}>
              <span style={{ color: '#888', marginRight: '10px' }}>
                [{entry.step.toString().padStart(2, '0')}]
              </span>
              <span style={{ color: statusColor, marginRight: '10px', fontWeight: 'bold' }}>
                {statusIcon}
              </span>
              <span style={{ color: '#61dafb', marginRight: '10px', fontWeight: 'bold' }}>
                {entry.action}:
              </span>
              <span style={{ color: '#abb2bf' }}>
                {entry.details}
              </span>
            </div>
            {entry.field_mappings && entry.field_mappings.length > 0 && (
              <div style={{
                marginLeft: '50px',
                fontSize: '12px',
                borderLeft: '2px solid #444',
                paddingLeft: '15px',
                marginTop: '6px'
              }}>
                {entry.field_mappings.map((mapping, idx) => (
                  <div key={idx} style={{ marginBottom: '3px', color: '#888' }}>
                    <span style={{ color: '#e5c07b' }}>{mapping.source}</span>
                    <span style={{ color: '#56b6c2' }}> → </span>
                    <span style={{ color: '#c678dd' }}>{mapping.target}</span>
                    <span style={{ color: '#abb2bf' }}> = </span>
                    <span style={{ color: '#98c379' }}>"{mapping.value}"</span>
                  </div>
                ))}
              </div>
            )}
            {entry.db_verification && (
              <div style={{
                marginLeft: '50px',
                fontSize: '12px',
                borderLeft: '2px solid #444',
                paddingLeft: '15px',
                marginTop: '8px',
                backgroundColor: '#0e1419',
                padding: '8px',
                borderRadius: '4px'
              }}>
                <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>
                  <span style={{ color: '#61dafb' }}>Database Verification:</span>
                  {entry.db_verification.matches ? (
                    <span style={{ color: '#4CAF50', marginLeft: '10px' }}>✓ MATCH</span>
                  ) : (
                    <span style={{ color: '#f44336', marginLeft: '10px' }}>✗ MISMATCH</span>
                  )}
                </div>
                <div style={{ color: '#888', fontSize: '11px', marginBottom: '4px' }}>
                  {entry.db_verification.model} ID: {entry.db_verification.id}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '6px' }}>
                  <div>
                    <div style={{ color: '#e5c07b', marginBottom: '4px', fontWeight: 'bold' }}>Expected:</div>
                    <div style={{ fontSize: '11px' }}>
                      {Object.entries(entry.db_verification.expected).map(([key, val]) => {
                        const actualVal = entry.db_verification!.actual[key]
                        const matches = String(val) === String(actualVal)
                        return (
                          <div key={key} style={{
                            color: matches ? '#888' : '#f44336',
                            marginBottom: '2px',
                            backgroundColor: matches ? 'transparent' : 'rgba(244, 67, 54, 0.1)'
                          }}>
                            <span style={{ color: '#c678dd' }}>{key}:</span> {String(val)}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#98c379', marginBottom: '4px', fontWeight: 'bold' }}>Actual (DB):</div>
                    <div style={{ fontSize: '11px' }}>
                      {Object.entries(entry.db_verification.actual).map(([key, val]) => {
                        const expectedVal = entry.db_verification!.expected[key]
                        const matches = String(val) === String(expectedVal)
                        return (
                          <div key={key} style={{
                            color: matches ? '#888' : '#f44336',
                            marginBottom: '2px',
                            backgroundColor: matches ? 'transparent' : 'rgba(244, 67, 54, 0.1)'
                          }}>
                            <span style={{ color: '#c678dd' }}>{key}:</span> {String(val)}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default TransferExecutionLogDisplay
